import { useContext, useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { AxiosError } from 'axios'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { ErrorMessage } from '@hookform/error-message'
import Button from 'components/button'
import Input from 'components/input'
import OtpInput from 'components/otp-input'
import ErrorView from 'components/error-view'
import { dlTrackEvent, dlSetUserInfo, createUserProperties } from 'components/dataLayer/dataLayer'
import formServices from 'services/form-builder-services'
import ProfileService from 'services/profile-services'
import textServices from 'services/text-services'
import useMainClass from 'hooks/use-main-cass'
import useRedirect from 'hooks/use-redirect'
import CONFIGS from 'build/Config'
import { useSession } from '../../contexts/session-context'
import { LoaderContext } from '../../contexts/loader-context'
import { Label, ErrorInput } from '../../theme/theme'
import { TitleWhatsappLogin, TextWhatsappLogin } from './text.style'

// --- Step 1: Phone Number ---

function PhoneStep({ setLoad, setGenericError, onSuccess }: {
  setLoad: (v: boolean) => void
  setGenericError: (v: number | null) => void
  onSuccess: (phone: string) => void
}) {
  const { register, handleSubmit, formState: { errors } } = useForm({
    resolver: yupResolver(formServices.formGenerateSchema(["phoneNumber"]))
  })
  const history = useHistory()

  const onFormSubmit = async (data: any) => {
    setLoad(true)
    try {
      dlTrackEvent('user_interaction', {
        action: 'click',
        element: 'enviar código whatsapp',
        section: 'whatsapp login'
      })

      const result = await ProfileService.whatsappSendOtp({ phoneNumber: data.phoneNumber })
      if (result.status === 200) {
        onSuccess(data.phoneNumber)
      }
    } catch (err) {
      const typedError = err as AxiosError
      const { code } = typedError.response?.data || {}
      setGenericError(code || 99999)
    } finally {
      setLoad(false)
    }
  }

  return (
    <div className="flex flex-col flex-1 md:pt-10">
      <form className="flex h-full justify-between flex-col gap-4 mx-7 mb-7 mt-7" onSubmit={handleSubmit(onFormSubmit)} noValidate>
        <div className="flex-1 flex flex-col gap-4">
          <TitleWhatsappLogin className="text-center text-5xl font-bold uppercase mb-4 whatsapp-login__title">
            {textServices.findByText('whatsapp-login/title', { es: 'Iniciar con WhatsApp', en: 'Login with WhatsApp' })}
          </TitleWhatsappLogin>
          <div className="gap-2 flex flex-col input-container">
            <Label className="text-start text-2xl font-bold uppercase input-label">
              {textServices.findByText('whatsapp-login/phone/label', { es: 'Número de celular', en: 'Phone number' })}
            </Label>
            <Input
              className="text-center saira font-bold text-sm"
              type="tel"
              placeholder={textServices.findByText('whatsapp-login/phone/placeholder', { es: 'Ingresa tu número de celular', en: 'Enter your phone number' }) as string}
              name="phoneNumber"
              {...register("phoneNumber")}
            />
            <ErrorInput className="input-error">
              <ErrorMessage errors={errors} name="phoneNumber" />
            </ErrorInput>
          </div>
          <div className="flex-1 flex flex-col items-center justify-center gap-4 text-center mx-7">
            <TextWhatsappLogin className="text-center text-base whatsapp-login__description">
              {textServices.findByText('whatsapp-login/phone/description', {
                es: 'Te enviaremos un código de verificación a tu WhatsApp.',
                en: 'We will send a verification code to your WhatsApp.'
              })}
            </TextWhatsappLogin>
          </div>
        </div>
        <Button>
          <p className="uppercase">
            {textServices.findByText('whatsapp-login/phone/button', { es: 'ENVIAR CÓDIGO', en: 'SEND CODE' })}
          </p>
        </Button>
        <button type="button" className="cursor-pointer" onClick={() => history.goBack()}>
          <p
            className="din text-center mt-3 hover:text-red-100 uppercase underline button-back"
            style={{ fontSize: 10 }}
          >
            {textServices.findByText('backButton', { es: 'VOLVER', en: 'BACK TO' })}
          </p>
        </button>
      </form>
    </div>
  )
}

// --- Step 2: OTP Verification ---

function OtpStep({ phoneNumber, setLoad, setSession, setUser, setGenericError, onBack, redirect }: {
  phoneNumber: string
  setLoad: (v: boolean) => void
  setSession: (s: any) => void
  setUser: (u: any) => void
  setGenericError: (v: number | null) => void
  onBack: () => void
  redirect: (() => void) | null
}) {
  const history = useHistory()
  const { handleSubmit, formState: { errors }, setValue } = useForm({
    resolver: yupResolver(formServices.formGenerateSchema(["otp"]))
  })

  const onFormSubmit = async (data: any) => {
    setLoad(true)
    try {
      dlTrackEvent('form_send', {
        element: 'verificar otp whatsapp',
        form_name: 'whatsapp login otp',
        section: 'whatsapp login'
      })

      const response = await ProfileService.whatsappVerifyOtp({
        code: data.otp,
        phone: phoneNumber
      })

      const { firebaseAccessToken, profile } = response.data

      // Create a mock session object compatible with the existing session pattern
      // The entire codebase uses (session as any)?._tokenResponse?.idToken for auth headers
      const mockSession = { _tokenResponse: { idToken: firebaseAccessToken } } as any

      dlTrackEvent('login', { method: 'whatsapp' }, profile?.hashId ?? profile?.id)
      gtag("event", "login", { method: "WhatsApp" })
      dlSetUserInfo(profile?.hashId ?? profile?.id)
      createUserProperties(profile, 'whatsapp')

      setSession(mockSession)
      setUser(profile)

      const external = localStorage.getItem('toexternal')
      if (external) {
        localStorage.removeItem('toexternal')
        return window.location.assign(decodeURI(JSON.parse(external)))
      }

      if (redirect) return redirect()

      const availableFields = CONFIGS?.notAvailableFields
      const isComplete = ((availableFields?.includes('nickName') ? false : !profile?.nickName) || !profile?.birthday || (!profile?.genre || !profile?.genreId))
      if (!CONFIGS?.signUp?.base && isComplete) return history.push('/information/update')

      return history.push('/home')
    } catch (err) {
      const typedError = err as AxiosError
      const { code } = typedError.response?.data || {}
      setGenericError(code || 99999)
    } finally {
      setLoad(false)
    }
  }

  const handleResend = async () => {
    setLoad(true)
    try {
      await ProfileService.whatsappSendOtp({ phoneNumber })
    } catch (err) {
      const typedError = err as AxiosError
      const { code } = typedError.response?.data || {}
      setGenericError(code || 99999)
    } finally {
      setLoad(false)
    }
  }

  return (
    <div className="flex flex-col flex-1 md:pt-10">
      <form className="flex h-full justify-between flex-col gap-4 mx-7 mb-7 mt-7" onSubmit={handleSubmit(onFormSubmit)} noValidate>
        <TitleWhatsappLogin className="text-center text-5xl font-bold uppercase mb-4 whatsapp-login__title">
          {textServices.findByText('whatsapp-login/otp/title', { es: 'Verificar código', en: 'Verify code' })}
        </TitleWhatsappLogin>
        <div className="flex-1 flex flex-col gap-4">
          <div className="gap-2 flex flex-col mb-3 input-container">
            <OtpInput setValue={setValue} />
            <ErrorInput className='input-error'>
              <ErrorMessage errors={errors} name="otp" />
            </ErrorInput>
          </div>
          <div className="flex-1 flex justify-center">
            <div className="h-full flex flex-col items-center text-center w-4/5 gap-4">
              <TextWhatsappLogin className="my-4 whatsapp-login__otp-description">
                {textServices.findByText('whatsapp-login/otp/description', {
                  es: <>Se envió un código de verificación a tu WhatsApp <b>{phoneNumber}</b></>,
                  en: <>A verification code was sent to your WhatsApp <b>{phoneNumber}</b></>
                })}
              </TextWhatsappLogin>
              <div className="flex flex-col items-center">
                <b>{textServices.findByText('otp/received', { es: '¿No lo recibiste?', en: 'Did you not receive it?' })}</b>
                <span
                  className="underline cursor-pointer ml-2"
                  onClick={() => {
                    dlTrackEvent('user_interaction', {
                      action: 'click',
                      element: 'reenviar código whatsapp',
                      section: 'whatsapp login'
                    })
                    handleResend()
                  }}
                >
                  {textServices.findByText('otp/resend', { es: 'Reenviar código', en: 'Resend code' })}
                </span>
              </div>
            </div>
          </div>
        </div>
        <Button>
          <p className="uppercase">
            {textServices.findByText('whatsapp-login/otp/button', { es: 'VERIFICAR', en: 'VERIFY' })}
          </p>
        </Button>
        <button type="button" className="cursor-pointer" onClick={onBack}>
          <p
            className="din text-center mt-3 hover:text-red-100 uppercase underline button-back"
            style={{ fontSize: 10 }}
          >
            {textServices.findByText('backButton', { es: 'VOLVER', en: 'BACK TO' })}
          </p>
        </button>
      </form>
    </div>
  )
}

// --- Main Component ---

const WhatsappLogin = () => {
  const [session, { setSession, setUser }] = useSession()
  const history = useHistory()
  const [{ url }, { redirect }] = useRedirect()
  const { setLoad, setHead, setFooter } = useContext(LoaderContext)
  const [step, setStep] = useState<1 | 2>(1)
  const [phoneNumber, setPhoneNumber] = useState('')
  const [genericError, setGenericError] = useState<number | null>(null)

  useMainClass('whatsapp-login')

  useEffect(() => {
    setHead(true)
    setFooter(true)
  }, [])

  useEffect(() => {
    if (session.user) {
      if (url) return redirect()
      history.push('/home')
    }
  }, [session.user])

  const handleMenu = () => {
    setGenericError(null)
  }

  if (genericError) {
    return <ErrorView code={genericError} buttonMenu={handleMenu} onRetry={() => setGenericError(null)} />
  }

  if (step === 2) {
    return (
      <OtpStep
        phoneNumber={phoneNumber}
        setLoad={setLoad}
        setSession={setSession}
        setUser={setUser}
        setGenericError={setGenericError}
        onBack={() => setStep(1)}
        redirect={url ? redirect : null}
      />
    )
  }

  return (
    <PhoneStep
      setLoad={setLoad}
      setGenericError={setGenericError}
      onSuccess={(phone: string) => {
        setPhoneNumber(phone)
        setStep(2)
      }}
    />
  )
}

export default WhatsappLogin
